import { Section } from '@vnetwork/ui/layout';
import { Card, CardBody } from '@vnetwork/ui/primitives';
import { type CDNDomain, useUpstreamConfig } from '@/api/manticore';
import { formatDateTime } from '../shared';

function DetailRow({ label, value, mono, full }: { label: string; value: React.ReactNode; mono?: boolean; full?: boolean }) {
  return (
    <div style={{ display: 'grid', gap: 2, gridColumn: full ? '1 / -1' : undefined, minWidth: 0 }}>
      <dt style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>{label}</dt>
      <dd
        style={{
          margin: 0,
          // Monospace renders visually larger at the same px, so step it down one
          // size to keep every value's apparent size consistent.
          fontSize: mono ? 'var(--vn-font-size-md)' : 'var(--vn-font-size-lg)',
          fontFamily: mono ? 'var(--vn-font-mono, ui-monospace, monospace)' : undefined,
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
        }}
      >
        {value || '—'}
      </dd>
    </div>
  );
}

export function DomainOverviewTab({ domain }: { domain: CDNDomain }) {
  // Detail endpoint returns the real CNAME under `cname_target`; fall back to the
  // list `cname` or the conventional edge host if neither is present.
  const cname =
    (domain.cname_target as string) ||
    (domain.cname as string) ||
    (domain.domain ? `${domain.domain}.cdn.cdnlswa.com` : '—');

  // The origin never lives on the domain record — it's in the upstream
  // (CDNOriginConfig) block, so read that aspect and surface the core binding's
  // origin URL(s). Falls back to the flat `origin` field only if upstream is
  // absent (older domains). Empty while the upstream read is in flight.
  const { data: upstream } = useUpstreamConfig(domain.id);
  const originUrls = (upstream?.config?.urls as string[] | undefined)?.filter(Boolean) ?? [];
  const hostHeader = (upstream?.config?.host_header as string | undefined) || undefined;
  const origin = originUrls.length ? originUrls.join(', ') : (domain.origin as string) || '—';
  return (
    <Section title="Overview" description="Edge status and connection details for this domain.">
      <Card>
        <CardBody>
          <dl
            style={{
              display: 'grid',
              gap: 'var(--vn-space-4)',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              margin: 0,
            }}
          >
            {/* Long values span the full width so they never wrap mid-domain. */}
            <DetailRow label="Domain" value={domain.domain} mono full />
            <DetailRow label="CNAME" value={cname} mono full />
            <DetailRow label="Origin" value={origin} mono full />
            {hostHeader && <DetailRow label="Host header" value={hostHeader} mono full />}
            <DetailRow label="Created" value={formatDateTime(domain.created_at)} />
            <DetailRow label="Last updated" value={formatDateTime(domain.updated_at)} />
          </dl>
        </CardBody>
      </Card>
    </Section>
  );
}
